import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, routes } from '@cedarjs/router';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import MODELS, { Model } from "src/const/Models";
import { Button, CSSObject, Divider, Icon, IconButton, ListItem, ListItemButton, ListItemIcon, ListItemText, TextField, Theme, Typography, styled, useTheme } from "@mui/material";
import { faChevronRight, faBars, faPlusCircle, faPlus, faXmark, faSearch } from "@fortawesome/free-solid-svg-icons";
import { t } from "i18next";
import { useMemo, useRef, useState } from "react";
import { groupBy, removeDiacritics } from "src/const/Helpers";
import { hasResourcePermission } from "src/lib/roles";
import { useAuth } from "src/auth";
import { Metadata } from "@cedarjs/web";

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: 29,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);


type LayoutProps = {
  model: Model
  children: React.ReactNode
}

const AdminLayout = ({
  model,
  children,
}: LayoutProps) => {

  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const searchRef = useRef<HTMLInputElement>();

  const handleDrawerOpen = () => {
    setOpen(true);
    searchRef.current.focus()
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };
  const { hasRole } = useAuth();
  const _MODELS = useMemo(() => {
    return MODELS.reduce((acc, model) => {
      const hasReadAccess = hasResourcePermission(hasRole, model.model, 'read')
      if (hasReadAccess && model.routes?.admin?.list)
        acc.push({
          ...model,
          labelForMatch: removeDiacritics(t('labels.' + model.label))
        })
      return acc
    }, [])
  }, [MODELS])

  const searchForMatch = useMemo(() => new RegExp(removeDiacritics(search), "i"), [search])
  let _models = _MODELS.filter(model => model.labelForMatch?.match(searchForMatch));
  const _sections = groupBy(_models, "group");

  const canCreate = hasResourcePermission(hasRole, model?.model, 'create')
  return (

    <Box position="relative" className="dark:bg-dark-950 bg-white">
      <Metadata title={`${t(`models.${model.model}`)} - ${t("titles.admin")}`} description={t(`models.${model.label}`)} />
      <AppBar position="absolute" elevation={0} open={open}>
        <Toolbar className="dark:bg-dark-1000 bg-slate-50 text-slate-700 dark:text-white/80">
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 5,
              ...(open && { display: 'none' }),
            }}
          >
            <FontAwesomeIcon icon={faBars} />
          </IconButton>

          <div className="flex justify-between items-center w-full">
            <Link to={routes[model.routes.admin.list]()} className="no-underline text-slate-700 dark:text-white/80">
              <Typography variant="h3">
                {t('models.' + model.model)}
              </Typography>
            </Link>
            {canCreate && <Link to={routes[model.routes.admin.new]()} className="">
              <Button variant="contained"><FontAwesomeIcon icon={faPlus} size="xl" className="mr-2" /> {t('labels.' + model.label)}</Button>
            </Link>}
          </div>
        </Toolbar>
      </AppBar>
      <div className="flex overflow-auto">
        <Drawer variant="permanent" open={open}
          PaperProps={{ style: { position: 'static' } }}>
          <DrawerHeader className="flex flex-col mt-2">
            <div className="flex justify-between items-center mb-4 w-full px-2 ">
              <Link to={routes.admin()} className="no-underline text-slate-700 dark:text-white/80">
                <Typography variant="h3" component="h2">Administration</Typography>
              </Link>
              <IconButton onClick={handleDrawerClose}>
                <FontAwesomeIcon icon={faChevronRight} />
              </IconButton>
            </div>

            <TextField
              inputRef={searchRef}
              onClick={handleDrawerOpen}
              onChange={(event) => setSearch(event.target.value)}
              value={search}
              className="dark:bg-dark-950 bg-white rounded mx-2"
              placeholder={`${t("actions.search")}…`}
              size="small"
              InputProps={{ endAdornment: search ? <FontAwesomeIcon icon={faXmark} onClick={() => setSearch("")} /> : <FontAwesomeIcon icon={faSearch} className="cursor-pointer" /> }}
            />
          </DrawerHeader>
          <List>
            {_models.filter(model => model.routes?.admin?.list).map((model) => (
              <Link to={routes[model.routes.admin.list]()} key={t('labels.' + model.label)} className="no-underline text-slate-700 dark:text-white/80 ">
                <ListItem key={model.routes.admin.list} disablePadding className="block">
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? 'initial' : 'center',
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : 'auto',
                        justifyContent: 'center',
                      }}
                    >
                      <FontAwesomeIcon icon={model.icon} className="w-5" />
                    </ListItemIcon>
                    <ListItemText primary={t('labels.' + model.label)} className="overflow-hidden text-ellipsis" sx={{ opacity: open ? 1 : 0 }} />
                  </ListItemButton>
                </ListItem>
              </Link>
            ))}
          </List>
        </Drawer>
        <Box component="main" position="relative" className="w-0 grow pt-16">
          {children}
        </Box>
      </div>
    </Box>
  )
}

export default AdminLayout
